import React, { useState, useRef, useEffect } from 'react';
import { Mic, Trash2, Loader, Download, FileText } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { usePlan } from '../context/PlanContext';
import { auth, saveVoiceMemo, deleteVoiceMemo, updateVoiceMemoTranscription } from '../firebase';

const toBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = reader.result;
    const commaIdx = typeof result === 'string' ? result.indexOf(',') : -1;
    if (commaIdx === -1) { reject(new Error('Audio encoding failed')); return; }
    resolve(result.substring(commaIdx + 1));
  };
  reader.onerror = () => reject(new Error('Audio encoding failed'));
  reader.readAsDataURL(blob);
});

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// How many days old is a memo (0 = today)
const daysSince = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

export default function Memos() {
  const { voiceMemos, setVoiceMemos, collapsedSections, toggleSection } = useAppContext();
  const { planConfig, isPro, isFeatureEnabled } = usePlan();

  const memoLimit = planConfig.voiceMemoLimit || 10;
  const maxSeconds = planConfig.voiceMemoMaxSeconds; // null = unlimited (Pro)
  const purgeDays = planConfig.voiceMemoPurgeDays; // null = no purge (Core)
  const canDownload = isFeatureEnabled('voiceMemoDownload');
  const canTranscribe = isFeatureEnabled('voiceMemoTranscription');

  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const timerRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const mediaRecorderRef = useRef(null);

  // ── Auto-purge on mount (Pro only) ─────────────────────────────────────────
  useEffect(() => {
    if (!isPro || !purgeDays) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    const toDelete = voiceMemos.filter(m => daysSince(m.date) >= purgeDays);
    if (toDelete.length === 0) return;

    // Delete from Firestore + state
    const purge = async () => {
      for (const memo of toDelete) {
        try { await deleteVoiceMemo(uid, memo.id); } catch {}
      }
      setVoiceMemos(prev => prev.filter(m => daysSince(m.date) < purgeDays));
    };
    purge();
  }, []); // only on mount

  const atLimit = voiceMemos.length >= memoLimit;

  const startRecording = async () => {
    if (atLimit) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const recMimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunks, { type: recMimeType });
        if (blob.size === 0) return;

        const memoId = Date.now().toString();
        const duration = recordingTimeRef.current;
        const title = `Voice Memo ${new Date().toLocaleTimeString()}`;
        const date = new Date().toISOString();

        setIsSaving(true);
        setSaveError('');

        try {
          const uid = auth.currentUser?.uid;
          if (!uid) throw new Error('Not authenticated');

          const audioBase64 = await toBase64(blob);
          await saveVoiceMemo(uid, memoId, { audioBase64, mimeType: recMimeType, duration, date, title });

          const newMemo = { id: memoId, audioBase64, mimeType: recMimeType, duration, date, title };
          setVoiceMemos(prev => [newMemo, ...prev]);

          // Pro transcription (fail silently)
          if (canTranscribe) {
            setIsTranscribing(true);
            try {
              const resp = await fetch('/api/transcribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ audioBase64, mimeType: recMimeType }),
              });
              if (resp.ok) {
                const { transcript } = await resp.json();
                if (transcript) {
                  await updateVoiceMemoTranscription(uid, memoId, transcript);
                  setVoiceMemos(prev => prev.map(m =>
                    m.id === memoId ? { ...m, transcription: transcript } : m
                  ));
                }
              }
            } catch {
              // fail silently
            } finally {
              setIsTranscribing(false);
            }
          }
        } catch (err) {
          console.error('Failed to save voice memo:', err);
          setSaveError(err.message === 'Audio encoding failed'
            ? 'Failed to encode audio. Please try again.'
            : 'Failed to save memo. Check your connection and try again.');
        } finally {
          setIsSaving(false);
        }
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      setSaveError('');
      recordingTimeRef.current = 0;

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          recordingTimeRef.current = next;

          // Core 120s auto-stop
          if (maxSeconds !== null && next >= maxSeconds) {
            setTimeout(() => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
                setIsRecording(false);
                clearInterval(timerRef.current);
              }
            }, 0);
            return maxSeconds;
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      setSaveError('Microphone access denied. Please allow microphone access in your browser settings.');
      console.error('Recording error:', err);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const deleteMemo = async (id) => {
    const uid = auth.currentUser?.uid;
    setVoiceMemos(prev => prev.filter(m => m.id !== id));
    try { if (uid) await deleteVoiceMemo(uid, id); } catch {}
  };

  const downloadMemo = (memo) => {
    const byteChars = atob(memo.audioBase64);
    const byteNums = new Array(byteChars.length).fill(0).map((_, i) => byteChars.charCodeAt(i));
    const blob = new Blob([new Uint8Array(byteNums)], { type: memo.mimeType || 'audio/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${memo.title || 'voice-memo'}.webm`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Core: pulsing warning at 110–119s
  const showWarning = !isPro && maxSeconds !== null && isRecording && recordingTime >= 110 && recordingTime < maxSeconds;
  const secsLeft = maxSeconds !== null ? maxSeconds - recordingTime : null;

  return (
    <div className="view">
      <h1 className="view-title">🎙️ Voice Memos</h1>

      {/* Recording Controls */}
      <div className="record-card">
        <div className="record-display">
          {isSaving ? (
            <div className="recording-active">
              <Loader size={48} style={{ animation: 'authSpin .8s linear infinite', color: '#00ff99' }} />
              <div className="recording-label">Saving memo...</div>
              {isTranscribing && <div className="recording-label" style={{fontSize:12,opacity:.6}}>Transcribing...</div>}
            </div>
          ) : isRecording ? (
            <div className="recording-active">
              <div className="recording-pulse" style={showWarning ? { borderColor: '#ffc800' } : {}}></div>
              <div className="recording-time">{formatTime(recordingTime)}</div>
              {showWarning ? (
                <div className="recording-label" style={{
                  color: '#ffc800', fontWeight: 700,
                  animation: 'authSpin 0s', // re-use keyframe container but just pulse opacity
                }}>
                  ⚠️ {secsLeft} second{secsLeft !== 1 ? 's' : ''} remaining
                </div>
              ) : (
                <div className="recording-label">
                  Recording{maxSeconds ? ` (Max ${formatTime(maxSeconds)})` : ''}
                </div>
              )}
            </div>
          ) : (
            <div className="recording-ready">
              <Mic size={48} />
              <div className="recording-label">
                {atLimit
                  ? `Limit reached (${memoLimit} memos)`
                  : 'Ready to Record'}
              </div>
            </div>
          )}
        </div>

        {atLimit ? (
          <div style={{
            padding: '14px 16px', background: 'rgba(255,100,100,.08)',
            border: '1px solid rgba(255,100,100,.2)', borderRadius: 12,
            color: 'rgba(255,255,255,.7)', fontSize: 13, textAlign: 'center', lineHeight: 1.5,
          }}>
            You've reached your {memoLimit} memo limit.{' '}
            {isPro
              ? 'Delete a memo to record a new one.'
              : <>Upgrade to Pro for 25 memos &amp; unlimited length.</>}
          </div>
        ) : (
          <button
            className={`btn-record ${isRecording ? 'recording' : ''}`}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : isRecording ? '⏹ Stop Recording' : '🎤 Start Recording'}
          </button>
        )}

        {saveError && (
          <div style={{marginTop:12,padding:'10px 14px',background:'rgba(255,77,77,.1)',border:'1px solid rgba(255,77,77,.3)',borderRadius:10,color:'#ff6b6b',fontSize:13,textAlign:'center'}}>
            {saveError}
          </div>
        )}
      </div>

      {/* Memos List */}
      <div className="section-card">
        <div className="section-hdr section-hdr-toggle" onClick={() => toggleSection('memos-list')} style={{marginBottom:'16px'}}>
          <h2>Your Voice Memos ({voiceMemos.length} / {memoLimit})</h2>
          <span className={`section-chevron ${collapsedSections['memos-list'] ? 'chevron-collapsed' : ''}`}>▼</span>
        </div>
        <div className={`section-collapse-content ${collapsedSections['memos-list'] ? 'section-collapsed' : ''}`}>
          {voiceMemos.length === 0 ? (
            <div className="empty-state">No voice memos yet. Tap Record to start! 🎙️</div>
          ) : (
            <div className="memo-list">
              {voiceMemos.map(memo => {
                const age = daysSince(memo.date);
                const expiresIn = purgeDays ? purgeDays - age : null;
                const showPurgeWarn = isPro && purgeDays && age >= 23 && age < purgeDays;

                return (
                  <div key={memo.id} className="memo-row">
                    <div className="memo-row-top">
                      <div className="memo-left">
                        <Mic size={20} className="memo-icon" />
                        <div className="memo-info">
                          <div className="memo-title">{memo.title}</div>
                          <div className="memo-meta">
                            {formatTime(memo.duration)} • {new Date(memo.date).toLocaleDateString()}
                          </div>
                          {showPurgeWarn && (
                            <div style={{fontSize:11,color:'#ffc800',marginTop:2,fontWeight:600}}>
                              Deletes in {expiresIn} day{expiresIn !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        {canDownload ? (
                          <button
                            title="Download"
                            onClick={() => downloadMemo(memo)}
                            style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,.5)',padding:4,display:'flex'}}
                          >
                            <Download size={15} />
                          </button>
                        ) : (
                          <button
                            title="Download available on Pro"
                            style={{background:'none',border:'none',cursor:'default',color:'rgba(255,255,255,.2)',padding:4,display:'flex'}}
                          >
                            <Download size={15} />
                          </button>
                        )}
                        <button className="btn-delete-memo" onClick={() => deleteMemo(memo.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <audio
                      src={`data:${memo.mimeType || 'audio/webm'};base64,${memo.audioBase64}`}
                      controls
                      className="memo-player"
                    />

                    {/* Transcription (Pro only) */}
                    {canTranscribe && memo.transcription && (
                      <div style={{
                        marginTop:10, padding:'10px 12px',
                        background:'rgba(255,255,255,.04)',
                        border:'1px solid rgba(255,255,255,.08)',
                        borderRadius:10,
                      }}>
                        <div style={{
                          display:'flex',alignItems:'center',gap:6,
                          fontSize:11,fontWeight:700,color:'rgba(255,255,255,.4)',
                          marginBottom:6,textTransform:'uppercase',letterSpacing:.5,
                        }}>
                          <FileText size={12} /> Transcript
                        </div>
                        <div style={{fontSize:13,color:'rgba(255,255,255,.7)',lineHeight:1.5}}>
                          {memo.transcription}
                        </div>
                      </div>
                    )}
                    {canTranscribe && !memo.transcription && isTranscribing && (
                      <div style={{marginTop:8,fontSize:12,color:'rgba(255,255,255,.35)',fontStyle:'italic'}}>
                        Transcribing...
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
